import { MARKET_DATA_DATABENTO_RECENT_TAIL_MESSAGE } from "../constants/marketDataDisclaimers";
import type { ExecutionCandlesResponse } from "../types/trade";
import {
  isExecutionCandleTypeFinerThan,
  pickRequestedExecutionCandleType,
  pickResolvedExecutionCandleType,
} from "./executionCandleTimeframe";

const BACKFILL_COVERAGE_THRESHOLD = 0.85;

/** Shown when async OHLC backfill polling exhausts retries or times out. */
export const EXECUTION_CANDLES_SOURCE_UNAVAILABLE_MESSAGE =
  "We couldn't source your requested candles. Please try another candle timeframe.";

/** Legacy: TF coarsening used to set window_clip_reason with this prefix. */
export function isLegacyTfCoarseningClipReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  const r = reason.toLowerCase();
  return r.includes("exceeds_cap") || r.includes("execution_chart_span");
}

/** Legacy bad path — never chart synthesized sub-hourly bars from coarser HL data. */
export function isExecutionCandlesSynthesizedHistory(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data?.history_limit) return false;
  const hl = data.history_limit;
  if (hl.synthesized === true) return true;
  const reason = String(hl.reason ?? "").trim().toLowerCase();
  return reason === "hyperliquid_coarser_fallback";
}

/** True when BE soft-clamped the window for provider max history (200 with partial candles). */
export function isExecutionCandlesHistoryLimited(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data) return false;
  if (isExecutionCandlesSynthesizedHistory(data)) return false;
  if (data.history_limit?.clamped === true) return true;
  if (data.history_limit?.history_limited === true) return true;
  const reason = String(data.window_clip_reason ?? data.history_limit?.reason ?? "")
    .trim()
    .toLowerCase();
  return reason === "liquid_history_insufficient" || reason.endsWith("_history_insufficient");
}

export function getExecutionCandlesHistoryLimitNotice(
  data: ExecutionCandlesResponse | null | undefined
): ExecutionCandlesResponse["history_limit"] | null {
  if (!isExecutionCandlesHistoryLimited(data)) return null;
  return data?.history_limit ?? null;
}

export function getExecutionCandlesHistoryLimitMessage(
  data: ExecutionCandlesResponse | null | undefined
): string | null {
  const message = getExecutionCandlesHistoryLimitNotice(data)?.message?.trim();
  return message || null;
}

export function getExecutionCandlesSuggestedCandleTypes(
  data: ExecutionCandlesResponse | null | undefined
): string[] {
  const raw = getExecutionCandlesHistoryLimitNotice(data)?.suggested_candle_types;
  if (!raw?.length) return [];
  return raw.filter((value): value is string => typeof value === "string" && value.trim().length > 0);
}

/** Omits machine codes like `execution_chart_span_exceeds_cap_at_5m` (alert copy already explains). */
export function getUserFacingCandleResolutionReason(
  reason: string | null | undefined
): string | undefined {
  if (!reason || isLegacyTfCoarseningClipReason(reason)) return undefined;
  return reason;
}

export function executionCandlesCoverageRatio(
  data: ExecutionCandlesResponse | null | undefined
): number | null {
  if (!data) return null;
  const expected = data.candles_expected;
  const returned = data.candles_returned ?? data.candles?.length ?? 0;
  if (expected == null || expected <= 0) return null;
  return returned / expected;
}

export function isExecutionCandlesCoverageLow(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  const ratio = executionCandlesCoverageRatio(data);
  return ratio != null && ratio < BACKFILL_COVERAGE_THRESHOLD;
}

/** True when persisted candles are enough to chart without MD backfill. */
export function isExecutionCandlesCoverageSufficient(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data) return false;
  if (data.backfill_complete === true) return true;
  if (data.md_queue_skipped_reason === "db_coverage_sufficient") return true;
  const ratio = executionCandlesCoverageRatio(data);
  return ratio != null && ratio >= BACKFILL_COVERAGE_THRESHOLD;
}

/** True when BE accepted async OHLC backfill and expects strip polling. */
export function isExecutionCandlesBackfillAcceptedResponse(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data?.poll_url?.trim()) return false;
  if (data.status === "backfilling") return true;
  if (data.backfill_complete === false && data.job_taken === true) return true;
  return false;
}

/** True when MD/backfill is genuinely incomplete — not when only TF was coarsened. */
export function isExecutionCandlesBackfillIncomplete(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data) return false;
  if (isExecutionCandlesCoverageSufficient(data)) return false;
  if (isExecutionCandlesHistoryLimited(data)) return false;
  if (data.backfill_complete === false) return true;

  const msg = (data.message ?? "").toLowerCase();
  if (
    msg === "md_queue_backfill_incomplete" ||
    msg === "md_queue_timeout_partial" ||
    msg.includes("md_queue_backfill_incomplete") ||
    msg.includes("md_queue_timeout_partial")
  ) {
    return isExecutionCandlesCoverageLow(data);
  }

  if (data.window_clipped && isLegacyTfCoarseningClipReason(data.window_clip_reason)) {
    return false;
  }

  if (data.window_clipped) {
    return isExecutionCandlesCoverageLow(data);
  }

  return false;
}

export function isExecutionCandlesTypeCoarsened(
  data: ExecutionCandlesResponse | null | undefined,
  requestedCandleType?: string | null
): boolean {
  if (!data) return false;
  if (data.candle_type_coarsened === true) return true;
  if (data.candle_type_coarsened === false) return false;

  const resolved = pickResolvedExecutionCandleType(data);
  const requested =
    requestedCandleType ?? pickRequestedExecutionCandleType(data, data.candle_type ?? "");
  if (!resolved || !requested) return false;
  return isExecutionCandleTypeFinerThan(requested, resolved);
}

const PARTIAL_MESSAGE_COPY: Record<string, string> = {
  md_queue_backfill_incomplete:
    "Some candles are still loading from your broker. The chart may look incomplete until backfill finishes.",
  md_queue_timeout_partial:
    "Some candles are still loading from your broker. The chart may look incomplete until backfill finishes.",
};

export function isDatabentoRecentTailUnavailable(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data) return false;
  const reason = String(data.window_clip_reason ?? data.message ?? "")
    .trim()
    .toLowerCase();
  return reason.includes("databento_recent_tail_unavailable");
}

export function getDatabentoRecentTailMessage(
  data: ExecutionCandlesResponse | null | undefined
): string | null {
  return isDatabentoRecentTailUnavailable(data) ? MARKET_DATA_DATABENTO_RECENT_TAIL_MESSAGE : null;
}

/** User-visible partial-load copy; never treat TF coarsening as timeout/backfill failure. */
export function getExecutionCandlesPartialMessage(
  data: ExecutionCandlesResponse | null | undefined
): string | null {
  if (!data?.message || data.message === "no_trades_for_symbol") return null;
  if (isExecutionCandlesHistoryLimited(data)) return null;
  if (!isExecutionCandlesBackfillIncomplete(data)) return null;

  const known = PARTIAL_MESSAGE_COPY[data.message];
  if (known) return known;

  const msg = data.message.toLowerCase();
  if (msg.includes("timeout") && !isExecutionCandlesCoverageLow(data)) {
    return null;
  }

  return data.message;
}
